// ============================================================================
// AdHocModal — two variants sharing one shell (AFB-0063 3e says extend this
// modal, not add another):
//
//   • Default (Today): add an unplanned stop to TODAY only. Pick a facility
//     or type a free-text label. Writes an ad-hoc am_route_day_stops row;
//     never touches the template.
//
//   • Template (pass `template`): add to MY ROTATION with two scopes —
//     "My facilities" (default; schedules an owned facility via
//     am_route_set_schedule) and "Someone else's" (creates an 'assist' row).
//     Search covers facility AND account name; rows show the owning AM and,
//     where resolvable, the main cleaner — AMs know facilities by who runs
//     them.
// ============================================================================

import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import FacilityPicker from './facility-picker';
import { RagDot } from '../../../shared/components';
import { ServiceStateChip } from '../../account-management/stop-service-parts';
import {
  CADENCE_LABELS,
  WEEKDAYS,
  type DayOfWeek,
  type DirectoryFacility,
  type FacilityOption,
  type RouteCadence,
  type WeekParity,
} from '../hooks/use-am-routes-data';
import { overlayClickClose } from '../../../shared/overlay-close';
import { errorMessage } from '../../../shared/errors';

const CADENCES: RouteCadence[] = [
  'Weekly',
  'Biweekly',
  'Monthly',
  'Bimonthly',
  'As_Needed',
];

export interface TemplateAddContext {
  myEid: string;
  directory: DirectoryFacility[];
  directoryLoading?: boolean;
  onScheduleOwn: (input: {
    facility_id: string;
    day_of_week: DayOfWeek | null;
    cadence: RouteCadence;
    week_parity: WeekParity | null;
  }) => Promise<void> | void;
  onAddAssist: (input: {
    facility_id: string;
    stop_label: string;
    day_of_week: DayOfWeek | null;
    cadence: RouteCadence;
    week_parity: WeekParity | null;
  }) => Promise<void> | void;
}

export interface AdHocModalProps {
  facilityOptions?: FacilityOption[];
  onClose: () => void;
  onAdd?: (input: { facility_id: string | null; stop_label: string }) => Promise<void> | void;
  /** Present = template variant (add to rotation with scopes). */
  template?: TemplateAddContext;
}

type Mode = 'facility' | 'freeText';
type Scope = 'mine' | 'others';

export default function AdHocModal({
  facilityOptions = [],
  onClose,
  onAdd,
  template,
}: AdHocModalProps) {
  if (template) {
    return <TemplateAddBody template={template} onClose={onClose} />;
  }
  return (
    <TodayAddBody
      facilityOptions={facilityOptions}
      onClose={onClose}
      onAdd={onAdd}
    />
  );
}

// ----- Today variant (unchanged behaviour) ----------------------------------

function TodayAddBody({
  facilityOptions,
  onClose,
  onAdd,
}: {
  facilityOptions: FacilityOption[];
  onClose: () => void;
  onAdd?: (input: { facility_id: string | null; stop_label: string }) => Promise<void> | void;
}) {
  const [mode, setMode] = useState<Mode>('facility');
  const [facilityId, setFacilityId] = useState<string | null>(null);
  const [facilityName, setFacilityName] = useState<string | null>(null);
  const [freeText, setFreeText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const canSubmit =
    mode === 'facility' ? !!facilityId : freeText.trim().length > 0;

  async function go() {
    if (!canSubmit || !onAdd) return;
    setSubmitting(true);
    setErr(null);
    try {
      await onAdd(
        mode === 'facility'
          ? { facility_id: facilityId, stop_label: facilityName ?? 'Facility' }
          : { facility_id: null, stop_label: freeText },
      );
    } catch (e) {
      setErr(errorMessage(e, 'Could not add stop.'));
      setSubmitting(false);
    }
  }

  return (
    <ModalShell onClose={onClose}>
      <h3 className="text-base font-semibold text-tcs-text">Add a stop today</h3>
      <p className="mt-0.5 text-sm text-tcs-text-secondary">
        One-off for today only — not added to your rotation.
      </p>

      <div className="mt-3 flex gap-0.5 rounded-md border border-tcs-border bg-tcs-bg p-0.5 text-xs">
        <TabBtn active={mode === 'facility'} onClick={() => setMode('facility')}>
          Facility
        </TabBtn>
        <TabBtn active={mode === 'freeText'} onClick={() => setMode('freeText')}>
          Other / free text
        </TabBtn>
      </div>

      <div className="mt-3">
        {mode === 'facility' ? (
          <FacilityPicker
            options={facilityOptions}
            value={facilityId}
            onChange={(id, name) => {
              setFacilityId(id);
              setFacilityName(name);
            }}
          />
        ) : (
          <input
            type="text"
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            autoFocus
            placeholder="e.g. Drop supplies at warehouse, lunch w/ POC"
            className="w-full rounded-md border border-tcs-border bg-tcs-field p-2 text-base shadow-inner focus:border-tcs-accent focus:outline-none"
          />
        )}
      </div>

      <FooterButtons
        onClose={onClose}
        onSubmit={go}
        canSubmit={canSubmit}
        submitting={submitting}
        err={err}
        label="Add stop"
      />
    </ModalShell>
  );
}

// ----- Template variant (AFB-0063 3e) ---------------------------------------

function TemplateAddBody({
  template,
  onClose,
}: {
  template: TemplateAddContext;
  onClose: () => void;
}) {
  const [scope, setScope] = useState<Scope>('mine'); // defaults to own facilities
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<DirectoryFacility | null>(null);
  const [day, setDay] = useState<DayOfWeek | 'unscheduled'>('unscheduled');
  const [cadence, setCadence] = useState<RouteCadence>('Weekly');
  const [parity, setParity] = useState<WeekParity>('A');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const scoped = useMemo(
    () =>
      template.directory.filter((f) =>
        scope === 'mine'
          ? f.owner_eid === template.myEid
          : f.owner_eid !== template.myEid,
      ),
    [template.directory, template.myEid, scope],
  );

  // Search covers facility name AND account name (3e).
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return scoped.slice(0, 50);
    return scoped.filter(
      (f) =>
        (f.facility_name ?? '').toLowerCase().includes(q) ||
        (f.account_name ?? '').toLowerCase().includes(q),
    );
  }, [scoped, query]);

  const canSubmit = !!selected;

  async function go() {
    if (!selected) return;
    setSubmitting(true);
    setErr(null);
    const dayValue = day === 'unscheduled' ? null : day;
    const parityValue = cadence === 'Biweekly' ? parity : null;
    try {
      if (scope === 'mine') {
        await template.onScheduleOwn({
          facility_id: selected.facility_id,
          day_of_week: dayValue,
          cadence,
          week_parity: parityValue,
        });
      } else {
        await template.onAddAssist({
          facility_id: selected.facility_id,
          stop_label: selected.facility_name ?? 'Facility',
          day_of_week: dayValue,
          cadence,
          week_parity: parityValue,
        });
      }
    } catch (e) {
      setErr(errorMessage(e, 'Could not add the stop.'));
      setSubmitting(false);
    }
  }

  return (
    <ModalShell onClose={onClose}>
      <h3 className="text-base font-semibold text-tcs-text">
        Add a stop to my rotation
      </h3>
      <p className="mt-0.5 text-sm text-tcs-text-secondary">
        {scope === 'mine'
          ? 'Put one of your facilities on a day.'
          : "Volunteer to help at someone else's facility — theirs stays theirs."}
      </p>

      <div className="mt-3 flex gap-0.5 rounded-md border border-tcs-border bg-tcs-bg p-0.5 text-xs">
        <TabBtn
          active={scope === 'mine'}
          onClick={() => {
            setScope('mine');
            setSelected(null);
          }}
        >
          My facilities
        </TabBtn>
        <TabBtn
          active={scope === 'others'}
          onClick={() => {
            setScope('others');
            setSelected(null);
          }}
        >
          Someone else&rsquo;s
        </TabBtn>
      </div>

      <div className="mt-3">
        {selected ? (
          <div className="flex items-center justify-between gap-2 rounded-md border border-tcs-border bg-tcs-surface px-3 py-2 text-sm">
            <span className="flex min-w-0 items-center gap-2 text-tcs-text">
              <RagDot status={selected.account_status_rag} />
              <span className="min-w-0">
                <span className="flex min-w-0 items-center gap-1.5">
                  <span className="truncate">
                    {selected.facility_name ?? selected.facility_id}
                  </span>
                  <ServiceStateChip state={selected.service_state} size="sm" />
                </span>
                <SecondaryLine f={selected} showOwner={scope === 'others'} />
              </span>
            </span>
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="shrink-0 text-xs font-medium text-tcs-text-secondary hover:text-tcs-text"
            >
              Change
            </button>
          </div>
        ) : (
          <>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tcs-text-muted" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search facility or account…"
                autoFocus
                className="w-full rounded-md border border-tcs-border bg-tcs-field py-2 pl-9 pr-3 text-base shadow-inner focus:border-tcs-accent focus:outline-none"
              />
            </div>
            <ul className="mt-2 max-h-56 overflow-auto rounded-md border border-tcs-border">
              {template.directoryLoading && (
                <li className="px-3 py-3 text-sm text-tcs-text-muted">Loading…</li>
              )}
              {!template.directoryLoading && matches.length === 0 && (
                <li className="px-3 py-3 text-sm text-tcs-text-muted">
                  No facilities match{query ? ` “${query}”` : ''}.
                </li>
              )}
              {matches.map((f) => (
                <li key={f.facility_id}>
                  <button
                    type="button"
                    onClick={() => setSelected(f)}
                    className="flex w-full items-start gap-2 border-b border-tcs-border px-3 py-2.5 text-left text-sm last:border-0 hover:bg-tcs-bg-muted"
                  >
                    <span className="pt-0.5">
                      <RagDot status={f.account_status_rag} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex min-w-0 items-center gap-1.5 text-tcs-text">
                        <span className="truncate">
                          {f.facility_name ?? f.facility_id}
                        </span>
                        {/* Adding an ad-hoc stop at a paused site is the exact
                            decision this badge exists to inform. */}
                        <ServiceStateChip state={f.service_state} size="sm" />
                      </span>
                      <SecondaryLine f={f} showOwner={scope === 'others'} />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <MiniSelect
          label="Day"
          value={day}
          onChange={(v) => setDay(v as DayOfWeek | 'unscheduled')}
        >
          <option value="unscheduled">Unscheduled / as able</option>
          {WEEKDAYS.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </MiniSelect>
        <MiniSelect
          label="Cadence"
          value={cadence}
          onChange={(v) => setCadence(v as RouteCadence)}
        >
          {CADENCES.map((c) => (
            <option key={c} value={c}>
              {CADENCE_LABELS[c]}
            </option>
          ))}
        </MiniSelect>
        {cadence === 'Biweekly' && (
          <MiniSelect
            label="Week"
            value={parity}
            onChange={(v) => setParity(v as WeekParity)}
          >
            <option value="A">Week A</option>
            <option value="B">Week B</option>
            <option value="A_B">Both weeks</option>
          </MiniSelect>
        )}
      </div>

      <FooterButtons
        onClose={onClose}
        onSubmit={go}
        canSubmit={canSubmit}
        submitting={submitting}
        err={err}
        label={scope === 'mine' ? 'Add to my rotation' : 'Add as helper'}
      />
    </ModalShell>
  );
}

// Owner (other scope) · account · main cleaner — AMs recognize facilities by
// who owns them or who cleans them (3e note).
function SecondaryLine({
  f,
  showOwner,
}: {
  f: DirectoryFacility;
  showOwner: boolean;
}) {
  const parts: string[] = [];
  if (showOwner) parts.push(f.owner_name ?? 'No AM assigned');
  if (f.account_name) parts.push(f.account_name);
  if (f.cleaner_name) parts.push(`Cleaner: ${f.cleaner_name}`);
  if (parts.length === 0) return null;
  return (
    <span className="block truncate text-xs text-tcs-text-secondary">
      {parts.join(' · ')}
    </span>
  );
}

// ----- Shared shell + bits ---------------------------------------------------

function ModalShell({
  onClose,
  children,
}: {
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-tcs-scrim/60 p-0 sm:items-center sm:p-4"
      {...overlayClickClose(onClose)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-t-2xl border border-tcs-border bg-tcs-overlay p-5 shadow-2xl sm:rounded-xl"
      >
        {children}
      </div>
    </div>
  );
}

function FooterButtons({
  onClose,
  onSubmit,
  canSubmit,
  submitting,
  err,
  label,
}: {
  onClose: () => void;
  onSubmit: () => void;
  canSubmit: boolean;
  submitting: boolean;
  err: string | null;
  label: string;
}) {
  return (
    <>
      {err && <p className="mt-2 text-xs text-rag-red">{err}</p>}
      <div className="mt-5 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="h-10 rounded-lg border border-tcs-border bg-tcs-raised px-4 text-sm font-medium text-tcs-text hover:bg-tcs-bg-muted disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={!canSubmit || submitting}
          className="h-10 rounded-lg bg-tcs-inverse px-4 text-sm font-medium text-tcs-inverse-ink hover:bg-tcs-inverse-hover disabled:opacity-50"
        >
          {submitting ? 'Adding…' : label}
        </button>
      </div>
    </>
  );
}

function MiniSelect({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  children: React.ReactNode;
}) {
  return (
    <label className="inline-flex items-center gap-1.5 rounded-lg border border-tcs-border bg-tcs-bg px-2 py-1 text-xs text-tcs-text">
      <span className="text-tcs-text-muted">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent font-medium text-tcs-text focus:outline-none"
      >
        {children}
      </select>
    </label>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 rounded px-3 py-1.5 font-medium ${
        active ? 'bg-tcs-surface text-tcs-text' : 'text-tcs-text-secondary'
      }`}
    >
      {children}
    </button>
  );
}
